/** Step 3: what will be imported, what is skipped, and what blocks saving. */

import type { ReactNode } from "react";
import { Pill } from "@/components/ui/pill";
import { preview } from "@/lib/format";
import type { ImportPlan, RowCheck } from "./csv-mapping";

function rowStatus(c: RowCheck): { text: string; tone: "neutral" | "warn" | "err" } {
  if (c.noInput) return { text: "Skipped: no input", tone: "err" };
  if (c.repeatOf !== null) return { text: `Skipped: repeats row ${c.repeatOf}`, tone: "warn" };
  if (c.noExpected) return { text: "No expected answer", tone: "neutral" };
  return { text: "OK", tone: "neutral" };
}

function Line({ n, label, children }: { n: number; label: string; children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line-soft py-3 text-[13px] last:border-0">
      <span className="flex items-center gap-2.5">
        <Pill tone={n ? "warn" : "neutral"}>{n}</Pill>
        {label}
      </span>
      {children}
    </div>
  );
}

export function ImportCheck({ checks, plan }: { checks: RowCheck[]; plan: ImportPlan }) {
  return (
    <div className="flex flex-col gap-5">
      <section className="box flex flex-col px-5 py-4" aria-label="Checks">
        <div className="flex items-baseline justify-between pb-1">
          <h2 className="text-[15px] font-semibold">Checks</h2>
          <span className="text-sm">
            <strong className="font-mono">{plan.items.length}</strong>{" "}
            <span className="text-muted">of {checks.length} rows will be imported</span>
          </span>
        </div>
        {plan.noExpected > 0 && (
          <p role="status" className="mt-2 rounded-lg bg-warn-bg px-3.5 py-3 text-[13px] text-warn">
            {plan.noExpected} {plan.noExpected === 1 ? "row has" : "rows have"} no expected answer —
            evaluators that compare with one will skip them.
          </p>
        )}
        <Line n={plan.skippedNoInput} label="rows with no input">
          <span className="text-muted">Always skipped</span>
        </Line>
        <Line n={plan.noExpected} label="rows with no expected answer">
          <span className="text-muted">Imported anyway</span>
        </Line>
        <Line
          n={plan.skippedRepeats}
          label="rows repeating an earlier row (same metadata.key, or same input)"
        >
          <span className="text-muted">Only the first is kept</span>
        </Line>
      </section>
      <section className="box" aria-label="Preview">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-[15px] font-semibold">Preview</h2>
          <span className="text-xs text-muted">
            First {Math.min(checks.length, 20)} rows, as they will be saved
          </span>
        </div>
        <table className="tbl text-xs">
          <thead>
            <tr>
              <th>Row</th>
              <th>input</th>
              <th>expected_output</th>
              <th>output</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {checks.slice(0, 20).map((c) => {
              const s = rowStatus(c);
              return (
                <tr key={c.index}>
                  <td className="font-mono text-muted">{c.index + 2}</td>
                  <td className="max-w-[380px] font-mono">
                    <span className="clamp-2">{preview(c.item.input)}</span>
                  </td>
                  <td className="max-w-[300px]">
                    <span className="clamp-2">{preview(c.item.expected_output)}</span>
                  </td>
                  <td className="max-w-[300px]">
                    <span className="clamp-2">{preview(c.item.output)}</span>
                  </td>
                  <td>
                    <Pill tone={s.tone}>{s.text}</Pill>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
